import { useCallback } from 'react';
import { getIdsFromDependencyId, type DependencyRecord } from '../utils/dependencyUtils';

/**
 * Custom hook for managing dependency operations in React Flow dependency maps
 *
 * This hook creates wrapper functions for dependency operations that integrate
 * with the parent component's queuing system. It provides a consistent interface
 * for adding, removing, and bulk removing dependencies.
 *
 * Key features:
 * 1. **Add Dependencies**: Creates new dependencies with proper source context
 * 2. **Remove Dependencies**: Removes individual dependencies by UUIDs
 * 3. **Bulk Remove**: Removes multiple dependencies by edge IDs
 * 4. **ID Translation**: Converts React Flow edge IDs to dependency UUIDs
 * 5. **Permission Enforcement**: Blocks operations for users without edit permissions
 *
 * Architecture:
 * - Wraps parent callback functions with useCallback for performance
 * - Translates between React Flow edge IDs and dependency UUIDs
 * - Provides consistent error handling for dependency operations
 * - Integrates with throttled dispatch system in wrapper components
 * - Enforces edit permissions to prevent unauthorized dependency changes
 *
 * Usage Pattern:
 * ```typescript
 * const { addDependency, removeDependency, removeDependenciesByIds } = useDependencyHandler(
 *   dependencyRecords,
 *   queueDependencyAdd,     // Parent queuing function
 *   queueDependencyRemove,  // Parent queuing function
 *   canEdit                 // User edit permissions
 * );
 * ```
 *
 * @param initialDependencies - Current dependency records (for context/validation)
 * @param onAddDependency - Parent function to queue dependency addition
 * @param onRemoveDependency - Parent function to queue dependency removal
 * @param canEdit - Whether the user has edit permissions (required for modifications)
 * @returns Object with addDependency, removeDependency, and removeDependenciesByIds functions
 */
export function useDependencyHandler(
  initialDependencies: DependencyRecord[],
  onAddDependency: (sourceResultUuid: string, targetResultUuid: string) => void,
  onRemoveDependency: (sourceResultUuid: string, targetResultUuid: string) => void,
  canEdit: boolean,
) {
  const addDependency = useCallback(
    (sourceResultUuid: string, targetResultUuid: string, sourceObjectiveUuid: string) => {
      if (!canEdit) {
        console.warn('Dependency addition denied: user does not have edit permissions');
        return;
      }
      // Call the parent handler directly
      onAddDependency(sourceResultUuid, targetResultUuid);
    },
    [onAddDependency, canEdit],
  );

  const removeDependency = useCallback(
    (sourceResultUuid: string, targetResultUuid: string) => {
      if (!canEdit) {
        console.warn('Dependency removal denied: user does not have edit permissions');
        return;
      }
      // Call the parent handler directly
      onRemoveDependency(sourceResultUuid, targetResultUuid);
    },
    [onRemoveDependency, canEdit],
  );

  const removeDependenciesByIds = useCallback(
    (edgeIds: string[]) => {
      if (!canEdit) {
        console.warn('Dependency removal denied: user does not have edit permissions');
        return;
      }
      edgeIds.forEach((id) => {
        const ids = getIdsFromDependencyId(id);
        if (ids) {
          onRemoveDependency(ids.sourceId, ids.targetId);
        }
      });
    },
    [onRemoveDependency, canEdit],
  );

  return {
    addDependency,
    removeDependency,
    removeDependenciesByIds,
  };
}
